/**
 * Shared detection for "no connected bank / payout account yet" vs real API failures.
 * v2 API copy may say "payout account" while v1 used "bank account"; onboarding must
 * still show Connect account, not a blocking Alert-only state.
 */

/** Thrown by useGetConnectedBankAccount when the repository returns null (empty state). */
export const SYNTHETIC_NO_CONNECTED_BANK_MESSAGE =
  "Sorry, You do not have a bank account saved! Please, create one and try again!";

/**
 * True when `message` indicates the user has no connected bank/payout account yet
 * (empty state), not a system or validation failure.
 */
export function isEmptyConnectedBankAccountMessage(message: string | undefined): boolean {
  if (!message) return false;
  if (message === SYNTHETIC_NO_CONNECTED_BANK_MESSAGE) return true;
  const m = message.toLowerCase();
  if (m.includes("no bank account")) return true;
  if (m.includes("not found")) return true;
  if (
    m.includes("payout account") &&
    (m.includes("do not have") || m.includes("don't have") || m.includes("kindly create"))
  ) {
    return true;
  }
  return false;
}

/**
 * True when an API error payload represents "no connected bank account yet".
 * Matches `{ message }` bodies from httpClientV2 and numeric `status: 404` if present.
 */
export function isEmptyConnectedBankAccountError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  if ("status" in error && (error as {status: unknown}).status === 404) return true;
  const msg = (error as {message?: unknown}).message;
  if (typeof msg === "string") return isEmptyConnectedBankAccountMessage(msg);
  return false;
}
